import {
  IndicatorValues,
  OperationalEnergyCertification,
  getLocalAuthHeader,
  operationalEnergyApi,
} from "api-client";
import { makeAutoObservable } from "mobx";
import { buildingStore } from "store/Building/BuildingStore";

export interface TableColumns {
  [key: string]: string;
}

export interface CertificationValues {
  columns: TableColumns[];
  complete: boolean;
  hasData: boolean;
  modified: string;
  values: OperationalEnergyCertification["table_data"];
}

export interface CertificationsData {
  [key: string]: CertificationValues;
}

class OperationalEmissionsStore {
  certificationsData: CertificationsData | undefined = undefined;

  setCertificationsData(certificationsData: CertificationsData | undefined) {
    this.certificationsData = certificationsData;
  }

  async fetchSyncB6() {
    const buildingID = buildingStore.buildingID;
    if (!buildingID) return;
    const authHeader = getLocalAuthHeader();
    return await operationalEnergyApi.concularApiV1RoutersOperationalEnergySyncB6(
      buildingID,
      authHeader
    );
  }

  async fetchAllCertificationsData(building_id?: string) {
    if (!building_id) return;
    const authHeader = getLocalAuthHeader();
    return await operationalEnergyApi
      .concularApiV1RoutersOperationalEnergyOperationalEnergyAllCertifications(
        building_id,
        authHeader
      )
      .then(({ data }) => {
        data.has_data && data.certifications
          ? this.prepareData(data.certifications)
          : this.setCertificationsData(undefined);
      })
      .catch((error) =>
        console.error(error, "OperationalEnergyAllCertifications")
      );
  }

  prepareData(certifications: {
    [key: string]: OperationalEnergyCertification;
  }) {
    Object.entries(certifications).forEach(([key, value]) =>
      this.restructureData(key, value)
    );
  }

  restructureData(
    key: string,
    certificationData: OperationalEnergyCertification
  ) {
    const transformedData = Object.entries(
      certificationData?.table_data ?? {}
    ).map((rec) => {
      const values = rec[1].indicator_values
        ?.map((subRec: IndicatorValues) => {
          return {
            [subRec?.name]: subRec?.value ?? "",
            unit: subRec?.unit ?? "",
            value_per_m2_year: subRec?.value_per_m2_year ?? "",
          };
        })
        .flat();

      return {
        friendly_name: rec[1].friendly_name,
        description: rec[1].description,
        ...Object.assign({}, ...values),
      };
    });

    this.setCertificationsData({
      ...this.certificationsData,
      [key as string]: {
        columns: transformedData,
        complete: Boolean(certificationData?.complete),
        hasData: Boolean(certificationData?.has_data),
        modified: certificationData?.modified ?? "",
        values: certificationData?.table_data ?? {},
      },
    });
  }

  reset() {
    this.certificationsData = undefined;
  }

  constructor() {
    makeAutoObservable(this);
  }

  static instance: OperationalEmissionsStore;

  static getInstance(): OperationalEmissionsStore {
    if (!OperationalEmissionsStore.instance) {
      OperationalEmissionsStore.instance = new OperationalEmissionsStore();
    }
    return OperationalEmissionsStore.instance;
  }
}

export const operationalEmissionsStore =
  OperationalEmissionsStore.getInstance();
